import React, { useState, useCallback } from 'react';
import {
  Card, Input, Button, Typography, Space, Spin, Alert, Tag, Tabs,
  Row, Col, message, Divider, Badge, Drawer, List, Avatar, Tooltip,
  Empty, Popconfirm, Checkbox,
} from 'antd';
import {
  AudioOutlined, RobotOutlined, CopyOutlined, SearchOutlined,
  PlayCircleOutlined, BulbOutlined, ThunderboltOutlined, CheckCircleOutlined,
  SaveOutlined, HistoryOutlined, DeleteOutlined, LoadingOutlined,
  GlobalOutlined, EditOutlined, CheckOutlined, CloseOutlined,
  MessageOutlined, SelectOutlined,
} from '@ant-design/icons';

const { Title, Text } = Typography;
const { TextArea } = Input;

const API_BASE_URL = (() => {
  const { hostname, port, protocol } = window.location;
  if (!port || port === '80' || port === '443') return `${protocol}//${hostname}/api/v1`;
  if (port === '3000') return `http://${hostname}:8000/api/v1`;
  return `http://${hostname}:${port}/api/v1`;
})();

const token = () => localStorage.getItem('spo_auth_token');
const authHeaders = () => ({
  'Authorization': `Bearer ${token()}`,
  'Content-Type': 'application/json',
});

const PLATFORM_COLOR = {
  facebook: '#1877f2', youtube: '#ff0000', instagram: '#e1306c',
  twitter: '#1da1f2', tiktok: '#010101', unknown: '#888888',
};

const CONTENT_TYPES = [
  { key: 'fb_post',      label: 'Facebook 貼文',    color: '#1877f2', desc: '完整貼文＋hashtag' },
  { key: 'ig_caption',   label: 'Instagram',        color: '#e1306c', desc: '說明文字＋hashtag' },
  { key: 'video_script', label: '短影音腳本',        color: '#ff4d4f', desc: '60秒分鏡腳本' },
  { key: 'email',        label: 'Email 行銷信',      color: '#1890ff', desc: '主旨＋正文＋CTA' },
  { key: 'ad_headline',  label: '廣告標題',          color: '#faad14', desc: '3組主副標＋CTA' },
  { key: 'line_message', label: 'LINE 推播',         color: '#00b900', desc: '100字推播訊息' },
  { key: 'blog_outline', label: '部落格大綱',        color: '#722ed1', desc: 'SEO文章結構' },
];

const EXAMPLE_URLS = [
  { label: 'Facebook', url: 'https://www.facebook.com/share/r/1RhgCBvkhp/' },
];

// ── Parse markdown sections ────────────────────────────────────────────────
function parseAnalysis(text) {
  if (!text) return [];
  const lines = text.split('\n');
  const sections = [];
  let current = null;
  for (const line of lines) {
    if (/^#{1,3} /.test(line)) {
      if (current) sections.push(current);
      current = {
        id: `s-${sections.length}`,
        title: line.replace(/^#{1,3} /, '').trim(),
        content: '',
        note: '',
        checked: true,
        editing: false,
        noteOpen: false,
      };
    } else if (current) {
      current.content += (current.content ? '\n' : '') + line;
    }
  }
  if (current) sections.push(current);
  sections.forEach(s => { s.content = s.content.trim(); });
  if (sections.length === 0) {
    return [{ id: 's-0', title: '分析內容', content: text.trim(), note: '', checked: true, editing: false, noteOpen: false }];
  }
  return sections;
}

function buildPromptFromSections(sections) {
  return sections
    .filter(s => s.checked)
    .map(s => {
      const noteStr = s.note.trim() ? `\n\n【加註說明】${s.note.trim()}` : '';
      return `## ${s.title}\n${s.content}${noteStr}`;
    })
    .join('\n\n');
}

// ── Single section card ────────────────────────────────────────────────────
function SectionCard({ section, onChange }) {
  const [editDraft, setEditDraft] = useState(section.content);
  const [noteDraft, setNoteDraft] = useState(section.note);

  const update = (patch) => onChange({ ...section, ...patch });

  const startEdit = () => {
    setEditDraft(section.content);
    update({ editing: true });
  };
  const confirmEdit = () => {
    update({ content: editDraft, editing: false });
  };
  const cancelEdit = () => {
    setEditDraft(section.content);
    update({ editing: false });
  };
  const confirmNote = () => {
    update({ note: noteDraft, noteOpen: false });
  };
  const openNote = () => {
    setNoteDraft(section.note);
    update({ noteOpen: true });
  };

  const copyContent = () => {
    const text = section.note.trim()
      ? `${section.content}\n\n【加註】${section.note}`
      : section.content;
    navigator.clipboard.writeText(text);
    message.success('已複製');
  };

  const checked = section.checked;

  return (
    <div style={{
      border: `1px solid ${checked ? '#d6e4ff' : '#f0f0f0'}`,
      borderLeft: `4px solid ${checked ? '#1890ff' : '#d9d9d9'}`,
      borderRadius: 8,
      background: checked ? '#fafcff' : '#fafafa',
      marginBottom: 10,
      transition: 'all 0.2s',
    }}>
      {/* Section header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', borderBottom: checked ? '1px solid #e8f0fe' : 'none' }}>
        <Checkbox
          checked={checked}
          onChange={e => update({ checked: e.target.checked })}
        />
        <Text strong style={{ flex: 1, fontSize: 13, color: checked ? '#1890ff' : '#aaa' }}>
          {section.title}
        </Text>
        <Space size={4}>
          {section.note && (
            <Tooltip title={section.note}>
              <Tag color="orange" style={{ fontSize: 11, cursor: 'pointer' }} onClick={openNote}>
                📝 有加註
              </Tag>
            </Tooltip>
          )}
          <Tooltip title="加註說明">
            <Button size="small" type="text" icon={<MessageOutlined />}
              style={{ color: section.noteOpen ? '#faad14' : '#aaa' }}
              onClick={openNote} />
          </Tooltip>
          <Tooltip title={section.editing ? '取消編輯' : '編輯內容'}>
            <Button size="small" type="text"
              icon={section.editing ? <CloseOutlined /> : <EditOutlined />}
              style={{ color: section.editing ? '#ff4d4f' : '#aaa' }}
              onClick={section.editing ? cancelEdit : startEdit} />
          </Tooltip>
          <Tooltip title="複製此段">
            <Button size="small" type="text" icon={<CopyOutlined />}
              style={{ color: '#aaa' }}
              onClick={copyContent} />
          </Tooltip>
        </Space>
      </div>

      {/* Section body */}
      {checked && (
        <div style={{ padding: '10px 14px 10px 38px' }}>
          {section.editing ? (
            <Space direction="vertical" style={{ width: '100%' }} size={6}>
              <TextArea
                value={editDraft}
                onChange={e => setEditDraft(e.target.value)}
                autoSize={{ minRows: 3, maxRows: 12 }}
                style={{ fontSize: 13, lineHeight: 1.7 }}
              />
              <Space>
                <Button size="small" type="primary" icon={<CheckOutlined />} onClick={confirmEdit}>確認修改</Button>
                <Button size="small" onClick={cancelEdit}>取消</Button>
              </Space>
            </Space>
          ) : (
            <pre style={{
              margin: 0, fontSize: 13, lineHeight: 1.75,
              whiteSpace: 'pre-wrap', color: '#333',
              fontFamily: 'inherit',
            }}>
              {section.content}
            </pre>
          )}

          {/* Note area */}
          {section.noteOpen && (
            <div style={{ marginTop: 10, background: '#fffbe6', border: '1px solid #ffe58f', borderRadius: 6, padding: '8px 12px' }}>
              <Text style={{ fontSize: 12, color: '#d48806', display: 'block', marginBottom: 6 }}>📝 加註說明（會附加在送給 AI 的提示詞中）</Text>
              <TextArea
                value={noteDraft}
                onChange={e => setNoteDraft(e.target.value)}
                placeholder="在這裡補充說明、修正資訊、或對 AI 的特別指示..."
                autoSize={{ minRows: 2, maxRows: 6 }}
                style={{ fontSize: 13 }}
              />
              <Space style={{ marginTop: 6 }}>
                <Button size="small" type="primary" ghost icon={<CheckOutlined />} onClick={confirmNote}>確認加註</Button>
                <Button size="small" onClick={() => update({ noteOpen: false })}>取消</Button>
                {section.note && <Button size="small" danger onClick={() => { setNoteDraft(''); update({ note: '', noteOpen: false }); }}>清除加註</Button>}
              </Space>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Analysis editor with all sections ─────────────────────────────────────
function AnalysisEditor({ sections, onChange }) {
  const allChecked = sections.every(s => s.checked);
  const checkedCount = sections.filter(s => s.checked).length;

  const updateSection = (idx, updated) => {
    const next = sections.map((s, i) => i === idx ? updated : s);
    onChange(next);
  };

  const toggleAll = () => {
    onChange(sections.map(s => ({ ...s, checked: !allChecked })));
  };

  const copyAll = () => {
    const text = buildPromptFromSections(sections);
    navigator.clipboard.writeText(text);
    message.success(`已複製 ${checkedCount} 個段落`);
  };

  return (
    <div>
      {/* Toolbar */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        <Space size={6}>
          <Checkbox
            indeterminate={checkedCount > 0 && checkedCount < sections.length}
            checked={allChecked}
            onChange={toggleAll}
          />
          <Text style={{ fontSize: 12, color: '#666' }}>
            已選 {checkedCount} / {sections.length} 個段落（生成素材時只使用勾選段落）
          </Text>
        </Space>
        <Tooltip title="複製所有勾選段落的內容">
          <Button size="small" icon={<CopyOutlined />} onClick={copyAll}>複製已選段落</Button>
        </Tooltip>
      </div>

      {sections.map((section, idx) => (
        <SectionCard
          key={section.id}
          section={section}
          onChange={updated => updateSection(idx, updated)}
        />
      ))}
    </div>
  );
}

// ── Platform tag ───────────────────────────────────────────────────────────
function PlatformTag({ platform }) {
  const p = (platform || 'unknown').toLowerCase();
  return <Tag color={PLATFORM_COLOR[p] || PLATFORM_COLOR.unknown} style={{ textTransform: 'capitalize' }}>{p}</Tag>;
}

// ── Main page ──────────────────────────────────────────────────────────────
export default function VideoTranscribePage() {
  const [url, setUrl] = useState('');
  const [analyzing, setAnalyzing] = useState(false);
  const [result, setResult] = useState(null);
  const [analyzeError, setAnalyzeError] = useState('');

  // Structured sections state
  const [sections, setSections] = useState([]);

  // Save state
  const [saving, setSaving] = useState(false);
  const [savedId, setSavedId] = useState(null);

  // History drawer
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyItems, setHistoryItems] = useState([]);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [historyQ, setHistoryQ] = useState('');
  const [loadingId, setLoadingId] = useState(null);

  // Marketing content generator state
  const [brandName, setBrandName] = useState('');
  const [productService, setProductService] = useState('');
  const [usp, setUsp] = useState('');
  const [targetAudience, setTargetAudience] = useState('');
  const [selectedTypes, setSelectedTypes] = useState(['fb_post', 'video_script', 'ig_caption']);
  const [generating, setGenerating] = useState(false);
  const [generated, setGenerated] = useState(null);
  const [genError, setGenError] = useState('');

  // Manual analysis input
  const [manualMode, setManualMode] = useState(false);
  const [manualAnalysis, setManualAnalysis] = useState('');
  const [manualSections, setManualSections] = useState([]);
  const [manualParsed, setManualParsed] = useState(false);

  // ── helpers ──────────────────────────────────────────────────────────────

  const handleAnalyze = async () => {
    if (!url.trim()) { message.warning('請輸入影片網址'); return; }
    setAnalyzing(true);
    setAnalyzeError('');
    setResult(null);
    setSections([]);
    setGenerated(null);
    setSavedId(null);
    try {
      const res = await fetch(`${API_BASE_URL}/video/transcribe`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ url: url.trim() }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || '分析失敗');
      setResult(data);
      setSections(parseAnalysis(data.analysis));
    } catch (e) {
      setAnalyzeError(e.message || '分析失敗，請確認網址是否正確');
    }
    setAnalyzing(false);
  };

  const parseManual = () => {
    if (!manualAnalysis.trim()) { message.warning('請貼上分析內容'); return; }
    setManualSections(parseAnalysis(manualAnalysis));
    setManualParsed(true);
  };

  const handleSave = async () => {
    const analysis = manualMode ? manualAnalysis : result?.analysis;
    if (!analysis?.trim()) { message.warning('沒有分析內容可儲存'); return; }
    setSaving(true);
    try {
      const info = result?.video_info || {};
      const res = await fetch(`${API_BASE_URL}/video/analyses`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          title: info.title || (manualMode ? '手動貼上分析' : '未命名分析'),
          url: url || null,
          platform: info.platform || null,
          video_info: info,
          transcript: result?.transcript || '',
          analysis,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || '儲存失敗');
      setSavedId(data.id);
      message.success('分析已儲存，可在歷史記錄中查詢');
      fetchHistory('');
    } catch (e) {
      message.error(e.message || '儲存失敗');
    }
    setSaving(false);
  };

  const fetchHistory = useCallback(async (q = '') => {
    setHistoryLoading(true);
    try {
      const params = new URLSearchParams({ limit: 50, offset: 0 });
      if (q) params.set('q', q);
      const res = await fetch(`${API_BASE_URL}/video/analyses?${params}`, { headers: authHeaders() });
      const data = await res.json();
      setHistoryItems(data.items || []);
      setHistoryTotal(data.total || 0);
    } catch {
      message.error('載入失敗');
    }
    setHistoryLoading(false);
  }, []);

  const openHistory = () => {
    setHistoryOpen(true);
    fetchHistory(historyQ);
  };

  const loadFromHistory = async (id) => {
    setLoadingId(id);
    try {
      const res = await fetch(`${API_BASE_URL}/video/analyses/${id}`, { headers: authHeaders() });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail);
      setResult({
        video_info: data.video_info || {},
        transcript: data.transcript || '',
        analysis: data.analysis || '',
      });
      setSections(parseAnalysis(data.analysis || ''));
      setUrl(data.url || '');
      setManualMode(false);
      setSavedId(id);
      setGenerated(null);
      setHistoryOpen(false);
      message.success(`已載入：${data.title}`);
    } catch (e) {
      message.error(e.message || '載入失敗');
    }
    setLoadingId(null);
  };

  const deleteHistory = async (id, e) => {
    e.stopPropagation();
    try {
      await fetch(`${API_BASE_URL}/video/analyses/${id}`, { method: 'DELETE', headers: authHeaders() });
      message.success('已刪除');
      fetchHistory(historyQ);
    } catch {
      message.error('刪除失敗');
    }
  };

  const handleGenerate = async () => {
    if (!brandName.trim()) { message.warning('請填寫品牌名稱'); return; }
    if (!productService.trim()) { message.warning('請填寫產品或服務'); return; }
    if (!selectedTypes.length) { message.warning('請至少選擇一種內容類型'); return; }

    const activeSections = manualMode ? manualSections : sections;
    const analysis = activeSections.length > 0
      ? buildPromptFromSections(activeSections)
      : (manualMode ? manualAnalysis : result?.analysis);

    if (!analysis?.trim()) { message.warning('請先分析影片或輸入分析內容'); return; }

    const checkedCount = activeSections.filter(s => s.checked).length;
    if (activeSections.length > 0 && checkedCount === 0) {
      message.warning('請至少勾選一個分析段落');
      return;
    }

    setGenerating(true);
    setGenError('');
    setGenerated(null);
    try {
      const res = await fetch(`${API_BASE_URL}/video/generate-content`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          analysis,
          transcript: result?.transcript || '',
          video_info: result?.video_info || {},
          brand_name: brandName,
          product_service: productService,
          usp, target_audience: targetAudience,
          content_types: selectedTypes,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || '生成失敗');
      setGenerated(data);
    } catch (e) {
      setGenError(e.message || '生成失敗');
    }
    setGenerating(false);
  };

  const copyText = (text) => {
    navigator.clipboard.writeText(text);
    message.success('已複製到剪貼簿');
  };

  const info = result?.video_info || {};
  const hasAnalysis = sections.length > 0 || (manualMode && (manualParsed ? manualSections.length > 0 : manualAnalysis.trim()));

  return (
    <div style={{ padding: '20px 24px', maxWidth: 1200, margin: '0 auto' }}>
      <Space direction="vertical" style={{ width: '100%' }} size={16}>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <AudioOutlined style={{ fontSize: 28, color: '#1890ff' }} />
            <div>
              <Title level={4} style={{ margin: 0 }}>影片分析 → 行銷素材生成</Title>
              <Text type="secondary" style={{ fontSize: 13 }}>
                分析任意競品影片，學習其行銷手法，自動生成自有品牌的行銷素材
              </Text>
            </div>
          </div>
          <Button icon={<HistoryOutlined />} onClick={openHistory}>
            分析記錄
            {historyTotal > 0 && <Badge count={historyTotal} overflowCount={99} style={{ marginLeft: 6 }} />}
          </Button>
        </div>

        {/* Step 1 */}
        <Card
          size="small"
          title={<Space><Badge count="1" style={{ background: '#1890ff' }} /><span>分析影片（Step 1）</span></Space>}
          extra={
            <Button size="small" type="link" onClick={() => { setManualMode(m => !m); setManualParsed(false); }}>
              {manualMode ? '改為影片 URL 模式' : '改為手動貼上分析'}
            </Button>
          }
        >
          {!manualMode ? (
            <Space direction="vertical" style={{ width: '100%' }} size={10}>
              <Space.Compact style={{ width: '100%' }}>
                <Input
                  size="large"
                  prefix={<PlayCircleOutlined style={{ color: '#bbb' }} />}
                  placeholder="貼上影片網址（Facebook、YouTube、Instagram、TikTok 等）"
                  value={url}
                  onChange={e => setUrl(e.target.value)}
                  onPressEnter={handleAnalyze}
                  disabled={analyzing}
                />
                <Button size="large" type="primary" icon={<SearchOutlined />}
                  loading={analyzing} onClick={handleAnalyze}>
                  {analyzing ? '分析中...' : '分析影片'}
                </Button>
              </Space.Compact>
              <Space size={6}>
                <Text type="secondary" style={{ fontSize: 12 }}>範例：</Text>
                {EXAMPLE_URLS.map(({ label, url: u }) => (
                  <Tag key={u} style={{ cursor: 'pointer' }} onClick={() => setUrl(u)}>{label}</Tag>
                ))}
              </Space>
            </Space>
          ) : (
            <Space direction="vertical" style={{ width: '100%' }} size={8}>
              <Text type="secondary" style={{ fontSize: 12 }}>
                直接貼上已有的影片分析內容，系統會自動拆解為可勾選、可加註的段落
              </Text>
              <TextArea
                rows={6}
                placeholder="貼上影片分析內容..."
                value={manualAnalysis}
                onChange={e => { setManualAnalysis(e.target.value); setManualParsed(false); }}
              />
              <Button icon={<SelectOutlined />} onClick={parseManual} type="primary" ghost>
                解析為可編輯段落
              </Button>
            </Space>
          )}
        </Card>

        {/* Loading */}
        {analyzing && (
          <Card size="small" style={{ textAlign: 'center', padding: '24px 0' }}>
            <Spin size="large" />
            <div style={{ marginTop: 12, color: '#666' }}>
              下載音訊 → Whisper 語音辨識 → Claude 分析（約 30-60 秒）
            </div>
          </Card>
        )}
        {analyzeError && (
          <Alert type="error" message={analyzeError} showIcon closable onClose={() => setAnalyzeError('')} />
        )}

        {/* Analysis Result */}
        {result && sections.length > 0 && (
          <Card
            size="small"
            bodyStyle={{ padding: 0 }}
          >
            {/* Video info bar */}
            <div style={{ padding: '10px 16px', borderBottom: '1px solid #f0f0f0', display: 'flex', alignItems: 'center', gap: 12 }}>
              {info.thumbnail && (
                <img src={info.thumbnail} alt="" style={{ width: 60, height: 40, objectFit: 'cover', borderRadius: 4 }} />
              )}
              <div style={{ flex: 1 }}>
                <PlatformTag platform={info.platform} />
                <div style={{ fontWeight: 600, fontSize: 13, marginTop: 2 }}>{info.title}</div>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {info.uploader} · {info.duration} · {info.view_count?.toLocaleString()} 次觀看
                </Text>
              </div>
              <Space>
                {savedId ? (
                  <Tag color="success" icon={<CheckCircleOutlined />}>已儲存</Tag>
                ) : (
                  <Button icon={<SaveOutlined />} loading={saving} onClick={handleSave} type="primary" ghost size="small">
                    儲存分析
                  </Button>
                )}
              </Space>
            </div>

            <Tabs
              defaultActiveKey="analysis"
              style={{ padding: '0 16px' }}
              items={[
                {
                  key: 'analysis',
                  label: <span><RobotOutlined /> AI 分析段落</span>,
                  children: (
                    <div style={{ paddingBottom: 16 }}>
                      <AnalysisEditor sections={sections} onChange={setSections} />
                    </div>
                  ),
                },
                {
                  key: 'transcript',
                  label: <span><AudioOutlined /> 逐字稿</span>,
                  children: (
                    <div style={{ paddingBottom: 16 }}>
                      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
                        <Button size="small" icon={<CopyOutlined />} onClick={() => copyText(result.transcript)}>
                          複製逐字稿
                        </Button>
                      </div>
                      <pre style={{
                        background: '#f6ffed', border: '1px solid #b7eb8f',
                        borderRadius: 6, padding: '12px 16px',
                        fontSize: 13, lineHeight: 1.8, whiteSpace: 'pre-wrap', margin: 0,
                      }}>
                        {result.transcript}
                      </pre>
                    </div>
                  ),
                },
              ]}
            />
          </Card>
        )}

        {/* Manual parsed sections */}
        {manualMode && manualParsed && manualSections.length > 0 && (
          <Card
            size="small"
            title={<Space><RobotOutlined /><span>已解析段落</span></Space>}
            extra={
              <Space>
                {savedId ? (
                  <Tag color="success" icon={<CheckCircleOutlined />}>已儲存</Tag>
                ) : (
                  <Button icon={<SaveOutlined />} size="small" loading={saving} onClick={handleSave} type="primary" ghost>
                    儲存分析
                  </Button>
                )}
              </Space>
            }
          >
            <AnalysisEditor sections={manualSections} onChange={setManualSections} />
          </Card>
        )}

        {/* Step 2: Generate */}
        {hasAnalysis && (
          <Card
            size="small"
            title={<Space><Badge count="2" style={{ background: '#52c41a' }} /><BulbOutlined style={{ color: '#52c41a' }} /><span>生成行銷素材（Step 2）</span></Space>}
          >
            <Space direction="vertical" style={{ width: '100%' }} size={14}>
              <Row gutter={12}>
                <Col span={12}>
                  <div style={{ marginBottom: 4 }}>
                    <Text strong style={{ fontSize: 13 }}>品牌名稱 <span style={{ color: '#ff4d4f' }}>*</span></Text>
                  </div>
                  <Input
                    placeholder="例：Comboware marcom AI Studio"
                    value={brandName}
                    onChange={e => setBrandName(e.target.value)}
                  />
                </Col>
                <Col span={12}>
                  <div style={{ marginBottom: 4 }}>
                    <Text strong style={{ fontSize: 13 }}>產品 / 服務 <span style={{ color: '#ff4d4f' }}>*</span></Text>
                  </div>
                  <Input
                    placeholder="例：AI SEO 年度服務方案"
                    value={productService}
                    onChange={e => setProductService(e.target.value)}
                  />
                </Col>
              </Row>
              <Row gutter={12}>
                <Col span={12}>
                  <div style={{ marginBottom: 4 }}><Text strong style={{ fontSize: 13 }}>核心優勢（USP）</Text></div>
                  <Input
                    placeholder="例：每天自動生成 SEO 文章，讓客戶主動找上門"
                    value={usp}
                    onChange={e => setUsp(e.target.value)}
                  />
                </Col>
                <Col span={12}>
                  <div style={{ marginBottom: 4 }}><Text strong style={{ fontSize: 13 }}>目標受眾</Text></div>
                  <Input
                    placeholder="例：台灣中小企業主、傳統製造業老闆"
                    value={targetAudience}
                    onChange={e => setTargetAudience(e.target.value)}
                  />
                </Col>
              </Row>

              <Divider style={{ margin: '8px 0' }} />

              <div>
                <Text strong style={{ fontSize: 13 }}>選擇要生成的素材類型</Text>
                <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {CONTENT_TYPES.map(({ key, label, color, desc }) => {
                    const checked = selectedTypes.includes(key);
                    return (
                      <div
                        key={key}
                        onClick={() => setSelectedTypes(prev =>
                          prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
                        )}
                        style={{
                          padding: '6px 14px', borderRadius: 20, cursor: 'pointer',
                          border: `2px solid ${checked ? color : '#d9d9d9'}`,
                          background: checked ? `${color}15` : '#fff',
                          transition: 'all 0.2s',
                        }}
                      >
                        <div style={{ fontWeight: checked ? 700 : 400, color: checked ? color : '#666', fontSize: 13 }}>{label}</div>
                        <div style={{ fontSize: 11, color: '#aaa' }}>{desc}</div>
                      </div>
                    );
                  })}
                </div>
              </div>

              <Button
                type="primary"
                size="large"
                icon={<ThunderboltOutlined />}
                loading={generating}
                onClick={handleGenerate}
                style={{ background: '#52c41a', borderColor: '#52c41a', width: '100%' }}
              >
                {generating ? '生成中，請稍候...' : `生成 ${selectedTypes.length} 種行銷素材`}
              </Button>
            </Space>
          </Card>
        )}

        {generating && (
          <Card size="small" style={{ textAlign: 'center', padding: '24px 0' }}>
            <Spin size="large" />
            <div style={{ marginTop: 12, color: '#666' }}>
              Claude 正在分析競品手法，為你的品牌生成原創行銷素材...
            </div>
          </Card>
        )}
        {genError && <Alert type="error" message={genError} showIcon closable onClose={() => setGenError('')} />}

        {/* Generated Results */}
        {generated && (
          <Card
            size="small"
            title={<Space><CheckCircleOutlined style={{ color: '#52c41a' }} /><span>生成完成｜{Object.keys(generated.contents).length} 種素材</span></Space>}
            bodyStyle={{ padding: 0 }}
          >
            <Tabs
              style={{ padding: '0 16px' }}
              items={Object.entries(generated.contents).map(([typeKey, content]) => ({
                key: typeKey,
                label: generated.labels?.[typeKey] || typeKey,
                children: (
                  <div style={{ paddingBottom: 16 }}>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
                      <Button size="small" icon={<CopyOutlined />} onClick={() => copyText(content)}>複製</Button>
                    </div>
                    <pre style={{
                      background: '#f9f9f9', border: '1px solid #f0f0f0',
                      borderRadius: 6, padding: '14px 16px',
                      fontSize: 13, lineHeight: 1.8, whiteSpace: 'pre-wrap', margin: 0,
                      fontFamily: 'inherit',
                    }}>
                      {content}
                    </pre>
                  </div>
                ),
              }))}
            />
          </Card>
        )}

      </Space>

      {/* History Drawer */}
      <Drawer
        title={
          <Space>
            <HistoryOutlined />
            <span>影片分析記錄</span>
            <Tag>{historyTotal} 筆</Tag>
          </Space>
        }
        placement="right"
        width={480}
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        bodyStyle={{ padding: 0 }}
      >
        <div style={{ padding: '12px 16px', borderBottom: '1px solid #f0f0f0' }}>
          <Input.Search
            placeholder="搜尋標題或分析內容..."
            value={historyQ}
            onChange={e => setHistoryQ(e.target.value)}
            onSearch={q => fetchHistory(q)}
            allowClear
            onClear={() => { setHistoryQ(''); fetchHistory(''); }}
          />
        </div>

        {historyLoading ? (
          <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div>
        ) : historyItems.length === 0 ? (
          <Empty description="尚無儲存的分析記錄" style={{ paddingTop: 60 }} />
        ) : (
          <List
            dataSource={historyItems}
            renderItem={item => (
              <List.Item
                key={item.id}
                style={{ padding: '12px 16px', cursor: 'pointer', transition: 'background 0.15s' }}
                onMouseEnter={e => e.currentTarget.style.background = '#f5f5f5'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                onClick={() => loadFromHistory(item.id)}
                actions={[
                  <Tooltip title="刪除" key="del">
                    <Popconfirm
                      title="確定刪除此分析記錄？"
                      okText="刪除" cancelText="取消"
                      okType="danger"
                      onConfirm={e => deleteHistory(item.id, e)}
                      onClick={e => e.stopPropagation()}
                    >
                      <Button
                        size="small" type="text" danger
                        icon={<DeleteOutlined />}
                        onClick={e => e.stopPropagation()}
                      />
                    </Popconfirm>
                  </Tooltip>,
                ]}
              >
                <List.Item.Meta
                  avatar={
                    item.thumbnail
                      ? <img src={item.thumbnail} alt="" style={{ width: 56, height: 38, objectFit: 'cover', borderRadius: 4 }} />
                      : <Avatar icon={<GlobalOutlined />} style={{ background: '#1890ff' }} />
                  }
                  title={
                    <Space size={4}>
                      {loadingId === item.id && <LoadingOutlined style={{ color: '#1890ff' }} />}
                      <Text ellipsis style={{ maxWidth: 240, fontSize: 13, fontWeight: 600 }}>{item.title}</Text>
                    </Space>
                  }
                  description={
                    <Space size={6} direction="vertical" style={{ gap: 2 }}>
                      <Space size={4}>
                        {item.platform && <PlatformTag platform={item.platform} />}
                        {item.uploader && <Text type="secondary" style={{ fontSize: 11 }}>{item.uploader}</Text>}
                      </Space>
                      <Text type="secondary" style={{ fontSize: 11 }}>
                        {item.created_at ? new Date(item.created_at).toLocaleString('zh-TW') : ''}
                      </Text>
                      {item.note && (
                        <Text type="secondary" ellipsis style={{ fontSize: 12, maxWidth: 280 }}>📝 {item.note}</Text>
                      )}
                    </Space>
                  }
                />
              </List.Item>
            )}
          />
        )}
      </Drawer>
    </div>
  );
}
